import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PromoButtons } from '../components/promos/PromoButtons';
import { useGuestTitle } from '../lib/guestRoute';
import { Star, ExternalLink, Loader2, Send, ArrowLeft, MessageSquare, ChevronRight, RotateCcw, Check, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { CardScanActions } from '../components/shared/CardScanActions';
import LanguageGate, { useLanguageGate, ChangeLanguageLink, ENABLED_LANGUAGES_FALLBACK } from '../components/shared/LanguageGate';
import BrandingShell from '../components/shared/BrandingShell';
import MedicalReferralCard from './reviewShield/MedicalReferralCard';
import MedicalConsultCard from './reviewShield/MedicalConsultCard';
import { cardSurfaceClass } from '../components/shared/cardSurface';
import PhotoAttach from './reviewShield/PhotoAttach';
import { getCategoryAccent } from '../components/shared/semanticAccents';
import './reviewShield/guestV2.css';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import { parseUtc } from '../lib/datetime';


const API_BASE = window.location.origin;
const DRAFT_TTL = 24 * 60 * 60 * 1000; // 24 hours
// Auto Flow pacing: stage 1 "Registering your voucher…", then the confirmation
// beat before the review page. Same two-stage shape as the Wi-Fi hand-off.
const LR_AUTO_STAGE1_MS = 6000;
const LR_AUTO_STAGE2_MS = 3000;

const PLATFORM_LABELS = {
    google_reviews: 'Google',
    facebook: 'Facebook',
    instagram: 'Instagram',
    trustpilot: 'Trustpilot',
    yelp: 'Yelp',
    tripadvisor: 'TripAdvisor'
};

/**
 * Loyal Review — post-save CTA label helper.
 *
 * Compliance note: the label MUST NEVER imply the reward is
 * contingent on leaving a review. Approved wording is
 * "Continue to <platform>" — a neutral navigation cue, not a
 * conditional or transactional phrase.
 *
 * The `t` function is passed in so all labels resolve via the
 * translation registry — no hardcoded platform strings.
 */
const getContinueCtaLabel = (t, platformKey) => {
    switch (platformKey) {
        case 'google_reviews':
            return t('loyalReview.voucher.continueToGoogle', 'Continue to Google Review');
        case 'facebook':
            return t('loyalReview.voucher.continueToFacebook', 'Continue to Facebook');
        case 'instagram':
            return t('loyalReview.voucher.continueToInstagram', 'Continue to Instagram');
        case 'tripadvisor':
            return t('loyalReview.voucher.continueToTripAdvisor', 'Continue to TripAdvisor');
        case 'yelp':
            return t('loyalReview.voucher.continueToYelp', 'Continue to Yelp');
        case 'trustpilot':
            return t('loyalReview.voucher.continueToTrustpilot', 'Continue to Trustpilot');
        default:
            return t('loyalReview.voucher.leavePublicReview', 'Leave a Public Review');
    }
};

const LOGO_SIZE_CLASSES = {
    small: 'max-h-16 max-w-[120px]',
    medium: 'max-h-20 max-w-[180px]',
    large: 'max-h-32 max-w-[280px]',
    very_large: 'max-h-44 max-w-[360px]'
};

// ---- Draft Storage Helpers ----
const getDraftKey = (cardId) => `review_draft_${cardId}`;

const saveDraft = (cardId, draft) => {
    try {
        localStorage.setItem(getDraftKey(cardId), JSON.stringify({ ...draft, timestamp: Date.now() }));
    } catch { /* storage full or unavailable */ }
};

const loadDraft = (cardId) => {
    try {
        const raw = localStorage.getItem(getDraftKey(cardId));
        if (!raw) return null;
        const draft = JSON.parse(raw);
        if (Date.now() - draft.timestamp > DRAFT_TTL) {
            localStorage.removeItem(getDraftKey(cardId));
            return null;
        }
        return draft;
    } catch {
        return null;
    }
};

const clearDraft = (cardId) => {
    try { localStorage.removeItem(getDraftKey(cardId)); } catch { /* ignore */ }
};

// ---- Save Indicator ----
const SaveIndicator = ({ status }) => {
    if (!status) return null;
    return (
        <div className="fixed bottom-4 right-4 z-50 animate-in fade-in slide-in-from-bottom-2 duration-300" data-testid="save-indicator">
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium backdrop-blur-md shadow-lg ${
                status === 'saving'
                    ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                    : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
            }`}>
                {status === 'saving' ? (
                    <><Loader2 className="w-3 h-3 animate-spin" />Saving...</>
                ) : (
                    <><Check className="w-3 h-3" />Saved</>
                )}
            </div>
        </div>
    );
};

// ---- Recovery Banner ----
const RecoveryBanner = ({ onResume, onDiscard, t }) => (
    <div className="w-full max-w-md mx-auto mb-4 animate-in fade-in slide-in-from-top-2 duration-300" data-testid="draft-recovery-banner">
        <div className="bg-amber-500/10 backdrop-blur-md border border-amber-500/30 rounded-xl p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
                <RotateCcw className="w-4 h-4 text-amber-400" />
                <p className="text-amber-200 text-sm font-medium">
                    {t('reviewShield.draftFound', 'We found an unfinished feedback response.')}
                </p>
            </div>
            <div className="flex gap-2 justify-center">
                <Button size="sm" onClick={onResume} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg" data-testid="draft-resume-btn">
                    {t('reviewShield.draftResume', 'Resume')}
                </Button>
                <Button size="sm" variant="ghost" onClick={onDiscard} className="text-slate-400 hover:text-white rounded-lg" data-testid="draft-discard-btn">
                    {t('reviewShield.draftStartOver', 'Start Over')}
                </Button>
            </div>
        </div>
    </div>
);


// ---- Global Review Destinations (inline for Review Shield integration) ----
const PLATFORM_COLORS_RS = {
    google_reviews: { bg: 'rgba(66, 133, 244, 0.12)', border: 'rgba(66, 133, 244, 0.3)' },
    facebook: { bg: 'rgba(24, 119, 242, 0.12)', border: 'rgba(24, 119, 242, 0.3)' },
    instagram: { bg: 'rgba(225, 48, 108, 0.12)', border: 'rgba(225, 48, 108, 0.3)' },
    trustpilot: { bg: 'rgba(0, 182, 122, 0.12)', border: 'rgba(0, 182, 122, 0.3)' },
    yelp: { bg: 'rgba(211, 35, 35, 0.12)', border: 'rgba(211, 35, 35, 0.3)' },
    tripadvisor: { bg: 'rgba(52, 224, 161, 0.12)', border: 'rgba(52, 224, 161, 0.3)' }
};

const GlobalReviewDestinations = ({ cardId, onSelect, submitting, t, feedbackId, setFeedbackId }) => {
    const [destinations, setDestinations] = useState([]);
    const [loadingDests, setLoadingDests] = useState(true);
    const [selecting, setSelecting] = useState(null);

    useEffect(() => {
        const fetchDests = async () => {
            try {
                const res = await axios.get(`${API_BASE}/api/global-review/${cardId}`);
                if (res.data.destinations) {
                    setDestinations(res.data.destinations);
                }
            } catch (err) {
                console.error('Failed to load destinations:', err);
            } finally {
                setLoadingDests(false);
            }
        };
        fetchDests();
    }, [cardId]);

    const handleSelect = async (dest) => {
        setSelecting(dest.platform);
        try {
            // v4.40.1 / v4.42 — One-Guest-One-Row: prefer the existing
            // feedback_id (from handleRatingSelect). Only create a row
            // here if we somehow arrived at the destination picker
            // without one — extremely rare edge case.
            let fbId = feedbackId;
            if (!fbId) {
                const feedbackRes = await axios.post(`${API_BASE}/api/review-shield/${cardId}/feedback`, {
                    rating: 5, category: null, message: null, email: null,
                });
                fbId = feedbackRes?.data?.feedback_id || null;
                if (fbId && setFeedbackId) setFeedbackId(fbId);
            }
            // Then record destination selection (platform routing)
            const selectRes = await axios.post(
                `${API_BASE}/api/global-review/${cardId}/select?platform=${dest.platform}`
            );
            // v4.40 — Fire the Operational Base "Continued" beacon
            // immediately before redirecting. sendBeacon is fire-and-
            // forget so the guest hand-off is never delayed. Silent
            // failure — the redirect must never be blocked by
            // observability instrumentation.
            try {
                if (fbId && typeof navigator !== 'undefined' && navigator.sendBeacon) {
                    const payload = new Blob(
                        [JSON.stringify({ feedback_id: fbId, platform: dest.platform })],
                        { type: 'application/json' },
                    );
                    navigator.sendBeacon(
                        `${API_BASE}/api/review-shield/${cardId}/continue-tracker`,
                        payload,
                    );
                }
            } catch (_) { /* never block redirect on tracker failure */ }
            window.location.href = selectRes.data.redirect_url || dest.url;
        } catch {
            window.location.href = dest.url;
        }
    };

    if (loadingDests) {
        return (
            <div className="flex justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
            </div>
        );
    }

    if (destinations.length === 0) {
        // Fallback to single review button
        return (
            <Button onClick={() => onSelect(true)} disabled={submitting} size="lg" className="w-full py-5 text-base font-semibold rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg" data-testid="review-shield-leave-review-btn">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <ExternalLink className="w-5 h-5 mr-2" />}
                {t('reviewShield.guestLeaveReview', 'Leave a Review')}
            </Button>
        );
    }

    return (
        <div className="space-y-2.5" data-testid="global-review-destinations-inline">
            <p className="text-slate-400 text-xs mb-1">{t('globalReview.chooseDestination', 'Where would you like to go?')}</p>
            {destinations.map((dest) => {
                const colors = PLATFORM_COLORS_RS[dest.platform] || PLATFORM_COLORS_RS.google_reviews;
                const isSelecting = selecting === dest.platform;
                return (
                    <button
                        key={dest.platform}
                        onClick={() => handleSelect(dest)}
                        disabled={selecting !== null}
                        className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-white transition-all duration-300 hover:scale-[1.02] disabled:opacity-50"
                        style={{ background: colors.bg, border: `1px solid ${colors.border}` }}
                        data-testid={`review-shield-global-dest-${dest.platform}`}
                    >
                        <span className="font-semibold text-sm flex-1 text-left">{dest.label}</span>
                        {isSelecting ? (
                            <Loader2 className="w-4 h-4 animate-spin text-white/60" />
                        ) : (
                            <ExternalLink className="w-4 h-4 text-white/40" />
                        )}
                    </button>
                );
            })}
        </div>
    );
};


const ReviewShield = () => {
    const params = useParams();
    const experienceId = params.experienceId || null;
    const experienceMode = !!experienceId;
    // `cardId` doubles as the generic resource id for draft keys and
    // shared helpers so the rest of the component stays byte-identical.
    const cardId = params.cardId || experienceId;
    // Single source of truth for the Review Shield API base. In Experience
    // mode this points at the permanent public Experience resolver instead
    // of the card endpoints — STRICTLY never an Automotive/vehicle route.
    const rsBase = experienceMode
        ? `${API_BASE}/api/review-shield-experience/${experienceId}`
        : `${API_BASE}/api/review-shield/${cardId}`;
    const { t, i18n } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    // Guest tab title = the client's business name, never the portal's.
    useGuestTitle(data?.organization_name);
    const [screen, setScreen] = useState('language');
    const [rating, setRating] = useState(0);
    const [hoveredStar, setHoveredStar] = useState(0);
    const [category, setCategory] = useState('');
    // The survey question behind the chosen category, stored server-side in
    // its own field (`category_question`) — never as the category itself.
    const [categoryQuestion, setCategoryQuestion] = useState(null);
    const [message, setMessage] = useState('');
    const [email, setEmail] = useState('');
    // Loyal Review — customer flow state. Only relevant when
    // data?.loyal_review_enabled is true; otherwise these stay untouched
    // and the standard positive/negative branching is byte-identical.
    const [lrEmail, setLrEmail] = useState('');
    const [lrError, setLrError] = useState('');
    const [lrSubmitting, setLrSubmitting] = useState(false);
    const [lrVoucher, setLrVoucher] = useState(null);
    // Auto Flow (card-level `loyal_review_auto_flow`): null = off (manual
    // screen, unchanged), 'registering' → 'registered' → redirect.
    const [lrAutoStage, setLrAutoStage] = useState(null);
    // Save state — the actual capability (native share vs. plain
    // download) is detected once at mount and drives both the button
    // label and the success message. This is the guardrail against
    // promising "Save to Photos" on a desktop that can only download.
    const lrVoucherCardRef = useRef(null);
    const [lrSaving, setLrSaving] = useState(false);
    const [lrSaved, setLrSaved] = useState(false);
    const [lrShareCapable, setLrShareCapable] = useState(false);
    useEffect(() => {
        try {
            if (
                typeof navigator !== 'undefined'
                && typeof navigator.share === 'function'
                && typeof navigator.canShare === 'function'
            ) {
                // Probe with a 1-byte dummy PNG — canShare is spec'd to
                // return true only when the platform accepts *file*
                // shares (which is what we need for "Save to Photos").
                const probe = new File(
                    [new Uint8Array([137, 80, 78, 71])],
                    'probe.png',
                    { type: 'image/png' },
                );
                setLrShareCapable(!!navigator.canShare({ files: [probe] }));
            }
        } catch (_) {
            setLrShareCapable(false);
        }
    }, []);
    // Voucher Terms & Conditions — mandatory acceptance before a
    // Loyal Review gift can be issued. The version string mirrors the
    // backend constant `LR_TC_VERSION`.
    const [lrTcAccepted, setLrTcAccepted] = useState(false);
    const [lrShowTerms, setLrShowTerms] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    // v4.38.0 — Pickup / Delivery contextual fields. All optional, all
    // ignored when the card's experience_type is dine_in (so the dine-in
    // flow stays byte-identical to v4.37).
    const [mealTime, setMealTime] = useState(null);
    const [categoriesArr, setCategoriesArr] = useState([]);
    const [contactEmail, setContactEmail] = useState('');
    const [contactPhone, setContactPhone] = useState('');
    // v1.2 — Photo attachments. Transient: lives only in component state
    // until submission, then travels with the multipart POST and is
    // discarded by the backend after email send. Never persisted.
    const [photos, setPhotos] = useState([]);
    // "What stood out" praise tags on a happy visit (only where a vertical
    // offers them). Recorded with the rating; never an improvement category.
    const [highlightsArr, setHighlightsArr] = useState([]);

    // VIP session
    const [vipSession, setVipSession] = useState(null);
    const [vipDelivered, setVipDelivered] = useState(null);

    // Draft recovery state
    const [pendingDraft, setPendingDraft] = useState(null);
    const [saveStatus, setSaveStatus] = useState(null); // null | 'saving' | 'saved'
    const saveTimerRef = useRef(null);
    const statusTimerRef = useRef(null);
    const draftRestoredRef = useRef(false);

    // v3.5.21 — Continuation flow: when an org has MULTIPLE global-review
    // destinations, the first tap on the circular CTA expands the destination
    // picker below it (preserves existing multi-platform behavior without
    // adding noise upfront). Single-destination orgs go straight via the
    // backend redirect_url, never expanding.
    const [destinationsExpanded, setDestinationsExpanded] = useState(false);

    // v4.40.1 — One-Guest-One-Row invariant. Whenever the client creates
    // a review_shield_feedback row (positive submit, LR issuance, or
    // multi-destination select), we STORE its id here so any subsequent
    // hand-off (Continue to Google/Facebook/…) re-uses the same row via
    // the continue-tracker beacon instead of creating a duplicate.
    const [feedbackId, setFeedbackId] = useState(null);

    // ---- Auto-Save (debounced 2s) ----
    const scheduleSave = useCallback(() => {
        if (draftRestoredRef.current === 'restoring') return; // Don't save while restoring
        setSaveStatus('saving');
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => {
            saveDraft(cardId, {
                rating,
                category,
                message,
                email,
                language: i18n.language,
                screen
            });
            setSaveStatus('saved');
            clearTimeout(statusTimerRef.current);
            statusTimerRef.current = setTimeout(() => setSaveStatus(null), 1500);
        }, 2000);
    }, [cardId, rating, category, message, email, screen, i18n.language]);

    // Trigger auto-save on meaningful state changes (skip initial load & confirmation)
    useEffect(() => {
        if (!data || screen === 'confirmation' || screen === 'language') return;
        if (rating === 0 && !message && !category) return; // Nothing to save
        scheduleSave();
        return () => clearTimeout(saveTimerRef.current);
    }, [rating, category, message, email, screen, scheduleSave, data]);

    // ---- Load Data & Check Draft ----
    useEffect(() => {
        loadData();
        // Check VIP session — card-only; Experiences have no VIP layer.
        if (!experienceMode) {
            fetch(`${API_BASE}/api/vouchers/vip/check/${cardId}`)
                .then(r => r.ok ? r.json() : { active: false })
                .then(d => { if (d.active) setVipSession(d); })
                .catch(() => {});
        }
        return () => {
            clearTimeout(saveTimerRef.current);
            clearTimeout(statusTimerRef.current);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cardId]);

    const deliverVip = async () => {
        if (!vipSession) return;
        try {
            const res = await fetch(`${API_BASE}/api/vouchers/vip/deliver/${vipSession.session_id}`, { method: 'POST' });
            if (res.ok) {
                const d = await res.json();
                setVipDelivered(d);
                setVipSession(null);
            }
        } catch { /* silent */ }
    };

    const loadData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(rsBase);
            const d = response.data;
            if (d.fallback) {
                window.location.href = d.redirect_url;
                return;
            }
            setData(d);

            // Resolve enabled languages (shared platform contract).
            const enabled = Array.isArray(d.enabled_languages) && d.enabled_languages.length
                ? d.enabled_languages
                : (d.language_mode === 'bilingual' ? ['en','fr'] : [d.language_mode || 'en']);

            const scopeKey = `rs:${cardId}`;
            let storedLang = null;
            try { storedLang = localStorage.getItem(`yulontap_lang_pref:${scopeKey}`); } catch {}
            if (storedLang && !enabled.includes(storedLang)) {
                // stale preference — re-prompt (spec 3b)
                try { localStorage.removeItem(`yulontap_lang_pref:${scopeKey}`); } catch {}
                storedLang = null;
            }

            // Check for existing draft
            const draft = loadDraft(cardId);
            if (draft && draft.rating > 0) {
                setPendingDraft(draft);
                // Draft language wins when still valid, else stored pref, else picker.
                const draftLang = draft.language && enabled.includes(draft.language) ? draft.language : null;
                const chosen = draftLang || storedLang;
                if (chosen) {
                    i18n.changeLanguage(chosen);
                    setScreen('rating');
                } else if (enabled.length === 1) {
                    i18n.changeLanguage(enabled[0]);
                    setScreen('rating');
                } else {
                    setScreen('language');
                }
            } else {
                // No draft — normal flow
                if (storedLang) {
                    i18n.changeLanguage(storedLang);
                    setScreen('rating');
                } else if (enabled.length === 1) {
                    i18n.changeLanguage(enabled[0]);
                    setScreen('rating');
                } else {
                    setScreen('language');
                }
            }
        } catch (err) {
            console.error('Failed to load Review Shield:', err);
            if (experienceMode) {
                setError(true);
                return;
            }
            try {
                window.location.href = `${API_BASE}/api/tap?card=${cardId}`;
            } catch {
                setError(true);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResumeDraft = () => {
        if (!pendingDraft) return;
        draftRestoredRef.current = 'restoring';
        setRating(pendingDraft.rating || 0);
        setCategory(pendingDraft.category || '');
        setMessage(pendingDraft.message || '');
        setEmail(pendingDraft.email || '');
        if (pendingDraft.language) i18n.changeLanguage(pendingDraft.language);

        // Restore to the appropriate screen
        const s = pendingDraft.screen;
        if (s === 'feedback' || s === 'category' || s === 'positive' || s === 'confirmation' || s === 'privateSuggestion') {
            setScreen(s);
        } else if (pendingDraft.rating > 0) {
            const threshold = Number(data?.public_review_threshold) || 4;
            const privateReview = !!data?.private_review_enabled;
            setScreen(pendingDraft.rating >= threshold
                ? (privateReview ? 'privateSuggestion' : 'positive')
                : 'category');
        } else {
            setScreen('rating');
        }
        setPendingDraft(null);
        setTimeout(() => { draftRestoredRef.current = true; }, 100);
    };

    const handleDiscardDraft = () => {
        clearDraft(cardId);
        setPendingDraft(null);
        setRating(0);
        setCategory('');
        setMessage('');
        setEmail('');
        setScreen('rating');
    };

    // ---- Handlers ----
    const handleLanguageSelect = (lang) => {
        try { localStorage.setItem(`yulontap_lang_pref:rs:${cardId}`, lang); } catch {}
        i18n.changeLanguage(lang);
        setScreen('rating');
    };

    const handleChangeLanguage = () => {
        try { localStorage.removeItem(`yulontap_lang_pref:rs:${cardId}`); } catch {}
        setScreen('language');
    };

    // v4.42 — Immediate feedback-row creation the moment the guest picks
    // a rating. This decouples the "an interaction happened" record from
    // the "guest continued to a public platform" event. If the guest
    // abandons at any point, the row still exists (with the fields we
    // have so far), and any subsequent step (category+message form, LR
    // issuance, Continue-to-Public-Reviews click) enriches THAT SAME row
    // — the One-Guest-One-Row invariant.
    //
    // Fire-and-forget: never block the UI transition if the POST fails.
    // The guest UX must feel instantaneous.
    const ensureFeedbackRowForRating = async (star) => {
        if (feedbackId) return feedbackId;  // already have a row
        try {
            const resp = await axios.post(
                `${rsBase}/feedback`,
                { rating: star, category: null, message: null, email: null },
            );
            const fid = resp?.data?.feedback_id || null;
            if (fid) setFeedbackId(fid);
            return fid;
        } catch (_) {
            return null;  // silent — the guest UX must not be blocked
        }
    };

    const handleRatingSelect = (star) => {
        setRating(star);
        // v4.42 — Create the interaction row NOW so it exists even if
        // the guest closes the browser before reaching the next screen.
        // The row is created in the background; we do not await it so
        // the transition remains snappy.
        ensureFeedbackRowForRating(star);
        // Public Review Threshold: ratings >= threshold → public Google review flow;
        // lower → internal feedback. Default 4 preserves legacy behaviour.
        const threshold = Number(data?.public_review_threshold) || 4;
        // Private Review — every rating stays in house. High raters get the
        // thank-you + optional suggestion screen; low raters run today's flow.
        const privateReview = !!data?.private_review_enabled;
        // v4.38.0 — Pickup / Delivery cards route 1-3★ guests through the
        // contextual flow (mealTime → multi-category → comment+contact)
        // instead of the legacy single-category screen. Dine-in is
        // BYTE-IDENTICAL to v4.37 — DO NOT alter that branch.
        const exp = data?.experience_type || 'dine_in';
        const lowPathTarget = data?.low_rating_flow === 'multi'
            ? 'categoriesMulti'
            : (exp === 'pickup' || exp === 'delivery')
                ? 'mealTime'
                : 'category';
        setTimeout(() => {
            // Private Review — no public step for any rating. High raters see
            // the thank-you + optional suggestion screen; low raters fall
            // through to the UNCHANGED low-rating flow below.
            if (privateReview) {
                setScreen(star >= threshold ? 'privateSuggestion' : lowPathTarget);
                return;
            }
            // Loyal Review branch — when the merchant enabled it AND
            // configured a source voucher, positive raters see the
            // gift/review choice screen first. Otherwise the existing
            // flow is byte-identical.
            const lrEnabled = (data?.loyal_review_enabled && data?.loyal_review_voucher_id) || data?.gift_generator_review;
            if (star >= threshold && lrEnabled) {
                setScreen('lrChoice');
                return;
            }
            setScreen(star >= threshold ? 'positive' : lowPathTarget);
        }, 300);
    };

    // The guest flow sends a CANONICAL category id (per the org's vertical);
    // the question text, when the choice came from a survey question, travels
    // separately in `categoryQuestion` so it can never become a category.
    const handleCategorySelect = (cat, question = null) => {
        setCategory(cat);
        setCategoryQuestion(question);
        setScreen('feedback');
    };

    // v1.2 — Photo-aware submit. When `photos` is empty we use the
    // legacy JSON endpoint to keep the wire shape unchanged. When the
    // guest attached evidence we switch to the multipart endpoint which
    // forwards the bytes to the manager email and discards them. Photos
    // are never persisted server-side per the v1.2 architectural decision.
    // v4.42 — Payload now carries `feedback_id` (when we already have a
    // row from handleRatingSelect) so the server enriches the same
    // interaction rather than inserting a duplicate.
    const submitFeedbackPayload = async (cardId, payload, attachedPhotos) => {
        const enrichedPayload = feedbackId ? { ...payload, feedback_id: feedbackId } : payload;
        if (!attachedPhotos || attachedPhotos.length === 0) {
            await axios.post(`${rsBase}/feedback`, enrichedPayload);
            return;
        }
        const form = new FormData();
        form.append('payload', JSON.stringify(enrichedPayload));
        for (const p of attachedPhotos) {
            form.append('photos', p.file, p.file.name);
        }
        await axios.post(
            `${rsBase}/feedback-with-photos`,
            form,
            { headers: { 'Content-Type': 'multipart/form-data' } },
        );
    };

    const handleSubmitFeedback = async () => {
        setSubmitting(true);
        try {
            const payload = { rating, category: category || null, category_question: categoryQuestion || null, message: message.trim() || null, email: email.trim() || null };
            await submitFeedbackPayload(cardId, payload, photos);
            clearDraft(cardId);
            setScreen('confirmation');
        } catch (err) {
            console.error('Failed to submit feedback:', err);
            setError('submit');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmitRatingOnly = async () => {
        setSubmitting(true);
        try {
            // v4.42 — Include feedback_id so this enriches the existing
            // row created at rating-select time (One-Guest-One-Row).
            const payload = {
                rating, category: category || null, message: null, email: null,
                ...(feedbackId ? { feedback_id: feedbackId } : {}),
            };
            await axios.post(`${rsBase}/feedback`, payload);
            clearDraft(cardId);
            setScreen('confirmation');
        } catch (err) {
            console.error('Failed to submit rating:', err);
            setError('submit');
        } finally {
            setSubmitting(false);
        }
    };

    // v4.38.0 — Pickup / Delivery submission. Carries meal_received_time
    // + multi-category array + optional comment + optional contact info.
    // The card knows the experience type; the server cross-validates.
    // Dine-in NEVER reaches this handler.
    const handleSubmitTakeoutDelivery = async () => {
        setSubmitting(true);
        try {
            const payload = {
                rating,
                experience_type: data?.experience_type,
                meal_received_time: mealTime || null,
                categories: categoriesArr.length ? categoriesArr : null,
                message: (message || '').trim() || null,
                contact_requested: !!(contactEmail.trim() || contactPhone.trim()),
                contact_email: contactEmail.trim() || null,
                contact_phone: contactPhone.trim() || null,
            };
            await submitFeedbackPayload(cardId, payload, photos);
            clearDraft(cardId);
            setScreen('confirmation');
        } catch (err) {
            console.error('Failed to submit takeout/delivery feedback:', err);
            setError('submit');
        } finally {
            setSubmitting(false);
        }
    };

    /**
     * Save the voucher as a PNG to the device photo library. The PNG now
     * comes from the PRODUCTION voucher artwork endpoint — the same
     * pipeline used for downloads and emails — so it carries the client
     * logo and the redemption QR code (the old client-side canvas
     * rendering bypassed that pipeline and produced the legacy design).
     * Web Share API when supported; direct download fallback.
     */
    const handleSaveVoucherToPhotos = async () => {
        if (!lrVoucher || lrSaving) return;
        setLrSaving(true);
        try {
            const res = await fetch(`${API_BASE}/api/vouchers/image/${lrVoucher.code}`);
            if (!res.ok) throw new Error('voucher_image_fetch_failed');
            const blob = await res.blob();
            const filename = `voucher-${lrVoucher.code}.png`;
            const file = new File([blob], filename, { type: 'image/png' });
            if (
                typeof navigator !== 'undefined'
                && navigator.canShare
                && navigator.canShare({ files: [file] })
                && typeof navigator.share === 'function'
            ) {
                try {
                    await navigator.share({
                        files: [file],
                        title: t('loyalReview.voucher.saveToPhotos', 'Save Voucher to Photos'),
                    });
                    setLrSaved(true);
                    return;
                } catch (shareErr) {
                    if (shareErr && shareErr.name === 'AbortError') return;
                }
            }
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            // Delay revoke — iOS Safari (which ignores the `download`
            // attribute and instead navigates to the blob URL inline
            // for a long-press → Save Image) needs the blob to stay
            // live for a few seconds while it renders.
            setTimeout(() => URL.revokeObjectURL(url), 15000);
            setLrSaved(true);
        } catch (e) {
            console.error('save voucher failed', e);
        } finally {
            setLrSaving(false);
        }
    };


    const handleLrIssueVoucher = async () => {
        setLrError('');
        const email = (lrEmail || '').trim().toLowerCase();
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
            setLrError(t('loyalReview.email.invalid'));
            return;
        }
        if (!lrTcAccepted) {
            setLrError(t('loyalReview.terms.mustAccept'));
            return;
        }
        setLrSubmitting(true);
        try {
            const backend = typeof window !== 'undefined' ? window.location.origin : '';
            // v4.42 — The row was already created by handleRatingSelect
            // (One-Guest-One-Row invariant). If, for any reason, we don't
            // have a feedbackId yet (network hiccup during the initial
            // create), fall back to creating it here so the LR issuance
            // has a row to attach voucher_status/voucher_code onto.
            let fbId = feedbackId;
            if (!fbId) {
                try {
                    const fbResp = await fetch(
                        `${backend}/api/review-shield/${cardId}/feedback`,
                        {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                rating, category: null, message: null, email: null,
                            }),
                        },
                    );
                    if (fbResp.ok) {
                        const fbData = await fbResp.json();
                        fbId = fbData?.feedback_id || null;
                        if (fbId) setFeedbackId(fbId);
                    }
                } catch (_) { /* voucher issuance is the primary action */ }
            }

            // Review Mode — the Gift Generator is the reward engine. Persist
            // the guest email/consent on the feedback row (lead capture), then
            // hand off to the Gift Generator scratch experience. The fixed
            // Loyal Review voucher is NOT issued here; the GG accept mints it.
            if (data?.gift_generator_review) {
                try {
                    await fetch(`${backend}/api/review-shield/${cardId}/feedback`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ rating, email, feedback_id: fbId }),
                    });
                } catch (_) { /* lead capture is best-effort; never block the reward */ }
                const q = new URLSearchParams({ flow: 'review' });
                if (fbId) q.set('fb', fbId);
                if (data?.redirect_url) q.set('rc', data.redirect_url);
                window.location.href = `/demo/${cardId}?${q.toString()}`;
                return;
            }

            const r = await fetch(`${backend}/api/public/loyal-review/${cardId}/issue`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email,
                    tc_accepted: true,
                    tc_language: (i18n?.language || 'en').slice(0, 2),
                    tc_version: 'v1.2',
                    feedback_id: fbId,
                }),
            });
            if (!r.ok) {
                const status = r.status;
                let key = 'loyalReview.errors.generic';
                if (status === 403) key = 'loyalReview.errors.disabled';
                else if (status === 409) key = 'loyalReview.errors.unavailable';
                setLrError(t(key));
                setLrSubmitting(false);
                return;
            }
            const voucher = await r.json();
            setLrVoucher(voucher);
            setLrSaved(false);
            setScreen('lrVoucher');
            // Auto Flow: the voucher is emailed by the issuance pipeline no
            // matter what happens on screen, so nothing here can be lost —
            // acknowledge, then hand the guest to the review page.
            if (data?.loyal_review_auto_flow && data?.redirect_url) setLrAutoStage('registering');
        } catch (e) {
            setLrError(t('loyalReview.errors.generic'));
        } finally {
            setLrSubmitting(false);
        }
    };

    const handlePositiveSubmit = async (goToReview) => {
        setSubmitting(true);
        try {
            // v4.40.1 — One-Guest-One-Row: if the LR issuance (or a
            // previous click) already created the feedback row for this
            // guest journey, DO NOT create a second row. Just fire the
            // continue-tracker beacon against the existing row and go.
            if (feedbackId) {
                const platform = data?.platform || 'unknown';
                const targetUrl = data?.redirect_url;
                if (highlightsArr.length) {
                    try {
                        await axios.post(`${rsBase}/feedback`, {
                            rating, highlights: highlightsArr, feedback_id: feedbackId,
                        });
                    } catch (_) { /* praise tags must never block the guest */ }
                }
                if (goToReview && targetUrl) {
                    try {
                        if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
                            const payload = new Blob(
                                [JSON.stringify({ feedback_id: feedbackId, platform })],
                                { type: 'application/json' },
                            );
                            navigator.sendBeacon(
                                `${API_BASE}/api/review-shield/${cardId}/continue-tracker`,
                                payload,
                            );
                        }
                    } catch (_) { /* never block redirect on tracker failure */ }
                    window.location.href = targetUrl;
                    return;
                }
                // If we can't redirect for some reason, fall back to
                // showing the internal feedback screen; do NOT re-POST.
                setScreen('feedback');
                return;
            }

            const response = await axios.post(`${rsBase}/feedback`, {
                rating, category: null, message: null, email: null,
                ...(highlightsArr.length ? { highlights: highlightsArr } : {}),
            });
            // Remember the row so any future click on this guest journey
            // (rare but possible if the guest bounces back and taps again)
            // enriches the same row.
            if (response.data?.feedback_id) setFeedbackId(response.data.feedback_id);
            clearDraft(cardId);
            if (goToReview && response.data?.redirect_url) {
                // v4.40 — Operational Base "Continued" column. Fire a
                // tracker beacon with the destination platform BEFORE
                // navigating away. `sendBeacon` is fire-and-forget so
                // it never delays or blocks the guest's redirect.
                try {
                    const fbId = response.data.feedback_id;
                    const platform = data?.platform || 'unknown';
                    if (fbId && typeof navigator !== 'undefined' && navigator.sendBeacon) {
                        const payload = new Blob(
                            [JSON.stringify({ feedback_id: fbId, platform })],
                            { type: 'application/json' },
                        );
                        navigator.sendBeacon(
                            `${API_BASE}/api/review-shield/${cardId}/continue-tracker`,
                            payload,
                        );
                    }
                } catch (_) { /* never block redirect on tracker failure */ }
                window.location.href = response.data.redirect_url;
            } else {
                setScreen('feedback');
            }
        } catch (err) {
            console.error('Failed to record feedback:', err);
            if (goToReview && data?.redirect_url) {
                window.location.href = data.redirect_url;
            }
        } finally {
            setSubmitting(false);
        }
    };

    // v3.5.21 — Tap on the new circular continuation CTA. We don't decide here
    // whether the redirect targets Google / Yelp / etc.; we just hand off to
    // the existing positive submission pipeline (which respects the org's
    // configured destination + backend redirect_url). For multi-destination
    // orgs we surface the picker so the guest still gets a choice.
    const handleContinueTap = () => {
        // Do NOT clear the stage: that dropped the screen back onto the manual
        // branch, which renders the QR strip — visible for the 1-2s the
        // redirect request takes. 'redirecting' keeps the Auto Flow markup on
        // screen and is ignored by the stage timer.
        setLrAutoStage((st) => (st ? 'redirecting' : st));
        const destinations = data?.global_review_destinations || [];
        const multiDest =
            data?.global_review_enabled &&
            data?.use_review_shield_with_global_review &&
            destinations.length > 1;
        if (multiDest) {
            setDestinationsExpanded(true);
            return;
        }
        handlePositiveSubmit(true);
    };

    // Auto Flow stage machine: "Registering…" (~5s) → "registered, redirecting"
    // (a beat to read it) → the review destination, no tap required.
    useEffect(() => {
        if (screen !== 'lrVoucher' || !['registering', 'registered'].includes(lrAutoStage)) return undefined;
        const ms = lrAutoStage === 'registering' ? LR_AUTO_STAGE1_MS : LR_AUTO_STAGE2_MS;
        const id = setTimeout(() => {
            if (lrAutoStage === 'registering') setLrAutoStage('registered');
            else handleContinueTap();
        }, ms);
        return () => clearTimeout(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [screen, lrAutoStage]);

    // Review Shield LOW-RATING RECOVERY categories.
    // SOURCE OF TRUTH = the organization's SAVED ACTIVE Question Manager
    // questions (data.questions, already active + ordered from the API). This
    // supports any vertical (restaurant, automotive, …) with NO hardcoded
    // vertical-specific categories. Food/Service/Other is only a last-resort
    // fallback used when the organization has not saved any questions at all.
    const orgCategories = (data?.questions || []);
    const hasOrgCategories = orgCategories.length > 0;
    const defaultCategories = [
        { id: 'food', icon: '\uD83C\uDF7D\uFE0F' },
        { id: 'service', icon: '\uD83E\uDD1D' },
        { id: 'other', icon: '\uD83D\uDCDD' },
    ];
    // ONE wording lookup. A vertical may override any guest string under
    // `reviewShield.byVertical.<vertical>.<key>`; anything it does not override
    // keeps the shared wording, so every existing vertical is untouched.
    const vt = (key, opts) => {
        const v = data?.vertical;
        const override = v ? t(`reviewShield.byVertical.${v}.${key}`, { ...(opts || {}), defaultValue: '' }) : '';
        return override || t(`reviewShield.${key}`, opts);
    };

    // Server-decided guest-flow shape + per-organization photo setting.
    const multiLowPath = data?.low_rating_flow === 'multi';
    const photosEnabled = data?.photos_enabled !== false;
    const highlightOptions = data?.highlight_options || [];
    // A choice on the multi-select screen reads the organization's OWN
    // question text when its managers have one for that topic (card settings),
    // and otherwise the shipped label — so a choice is never blank. Scoped to
    // that screen (clinics today); every other vertical keeps its own labels.
    const categoryLabel = (key) => {
        const lang = (i18n.language || 'en').slice(0, 2);
        const own = multiLowPath
            ? (data?.questions || []).find((q) => q?.category_id === key)
            : null;
        const text = own && (own[`text_${lang}`] || own.text_en);
        return (text && String(text).trim()) || t(`reviewShield.categoryLabels.${key}`);
    };

    // THE ORGANIZATION'S SAVED VOUCHER DESIGN, applied to the voucher card
    // drawn inside this flow. The issuance endpoint resolves it with the same
    // resolver the standalone voucher page and the PNG use. No saved design
    // (null) = today's green card, so every other organization is unchanged.
    const lrDesign = lrVoucher?.design || null;
    const lrAccentStyle = lrDesign?.accent
        ? { color: lrDesign.accent, WebkitTextFillColor: lrDesign.accent }
        : undefined;
    const lrTextStyle = lrDesign?.text ? { color: lrDesign.text } : undefined;
    const lrMutedStyle = lrDesign?.text ? { color: lrDesign.text, opacity: 0.7 } : undefined;

    // Shown only where a vertical defines it (clinics: urgent care goes to the
    // clinic or 911, never to a feedback form).
    // A vertical may also replace the photo helper text, so a clinic never
    // sees the restaurant examples ("missing items", "food quality", …).
    const photoHelper = data?.vertical
        ? t(`reviewShield.byVertical.${data.vertical}.photoHelper`, { defaultValue: '' })
        : '';
    // A vertical may add one short advisory line beside the photo button
    // (clinics: no people, no medical conditions). Advice only — it never
    // blocks, filters or rejects an upload.
    const photoNotice = data?.vertical
        ? t(`reviewShield.byVertical.${data.vertical}.photoNotice`, { defaultValue: '' })
        : '';
    // Clinic-only additions. `isMedical` keeps every other vertical's screens
    // byte-identical; the referral card ALSO needs the clinic's existing
    // Loyal Review switch to be on, exactly like the reward path itself.
    const isMedical = data?.vertical === 'medical';
    const showReferralCard = isMedical && !experienceMode
        && !!((data?.loyal_review_enabled && data?.loyal_review_voucher_id)
              || data?.gift_generator_review);
    const showConsultCard = isMedical && !experienceMode && !!cardId;
    const urgentNote = data?.vertical
        ? t(`reviewShield.byVertical.${data.vertical}.urgentNote`, { defaultValue: '' })
        : '';

    const currentLang = i18n.language || 'en';
    const localizedQuestion = (q) => (
        currentLang === 'fr' ? (q.text_fr || q.text_en)
        : currentLang === 'es' ? (q.text_es || q.text_en)
        : q.text_en
    );

    // ---- Render ----

    // VIP Delivery Display
    if (vipDelivered) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 flex items-center justify-center p-4" data-testid="vip-customer-delivery">
                <div className="w-full max-w-xs space-y-6 text-center">
                    <div className="w-16 h-16 rounded-full bg-red-600/20 flex items-center justify-center mx-auto">
                        <Crown className="w-8 h-8 text-red-400" />
                    </div>
                    <div className="space-y-1">
                        <h2 className="text-lg font-semibold text-white">{t('voucher.vipVoucher')}</h2>
                        <p className="text-4xl font-bold text-emerald-400">${vipDelivered.value}</p>
                        <p className="text-lg font-mono text-white tracking-wider mt-2">{vipDelivered.code}</p>
                    </div>
                    {vipDelivered.message && (
                        <p className="text-sm text-slate-400 italic">{vipDelivered.message}</p>
                    )}
                    <div className="bg-slate-900/50 backdrop-blur-sm rounded-lg p-3 text-left space-y-1 text-sm">
                        <div className="flex justify-between text-slate-400">
                            <span>{t('voucher.validUntil')}</span>
                            <span className="text-slate-200">{parseUtc(vipDelivered.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        </div>
                        {vipDelivered.organization_name && (
                            <div className="flex justify-between text-slate-400">
                                <span>{t('voucher.from')}</span>
                                <span className="text-slate-200">{vipDelivered.organization_name}</span>
                            </div>
                        )}
                    </div>
                    <a href={`/voucher/${vipDelivered.code}`} className="block w-full">
                        <Button data-testid="vip-save-photo-customer" variant="outline" className="w-full border-slate-700 text-white hover:bg-slate-800">
                            {t('voucher.saveToPhotos')}
                        </Button>
                    </a>
                    <CardScanActions cardId={experienceMode ? null : cardId} className="pt-2" />
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-emerald-400 mx-auto" />
                    <p className="text-slate-300 mt-4">{t('reviewShield.guestLoading', 'Loading...')}</p>
                    <CardScanActions cardId={experienceMode ? null : cardId} className="mt-6" />
                </div>
            </div>
        );
    }

    if (error === true && !data) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-red-900 flex items-center justify-center p-4">
                <div className="text-center max-w-md">
                    <h1 className="text-2xl font-bold text-white mb-2">{t('reviewShield.guestErrorTitle', 'Something went wrong')}</h1>
                    <p className="text-slate-300">{t('reviewShield.guestError', 'Unable to load. Please try again.')}</p>
                    <CardScanActions cardId={experienceMode ? null : cardId} className="mt-6" />
                </div>
            </div>
        );
    }

    if (!data) return null;

    const platformLabel = PLATFORM_LABELS[data.platform] || 'Review';
    const logoUrl = data.logo_url;
    const logoSize = data.logo_size || 'medium';
    const logoClass = LOGO_SIZE_CLASSES[logoSize] || LOGO_SIZE_CLASSES.medium;
    // v3.5.17 — Background wallpaper resolved from card; falls back to the
    // dark gradient inside BrandingShell when null. ALL screens of the flow
    // (language, rating, positive/negative branches, vip) share the same
    // background — single source of truth.
    const wallpaperUrl = data.background_image_url || null;
    // v3.5.19 — Card surface style resolved server-side. Falls back to
    // 'frosted' when API didn't return one (legacy cards / older endpoints).
    const cardStyle = data.card_style || 'frosted';
    const cardSurface = cardSurfaceClass(cardStyle, 'dark');
    // Guest Experience Version — presentation-only. 'v2' applies the
    // Enhanced visual layer (guestV2.css) to the SAME flow. Legacy / unset
    // cards resolve to 'v1' (Classic) and are byte-identical to before.
    const isV2 = data.guest_experience_version === 'v2';

    return (
        <BrandingShell
            wallpaperUrl={wallpaperUrl}
            variant="dark"
            data-testid="review-shield-page"
        >
            {screen !== 'language' && (
                <ChangeLanguageLink
                    enabled={Array.isArray(data?.enabled_languages) && data.enabled_languages.length
                        ? data.enabled_languages
                        : (data?.language_mode === 'bilingual' ? ['en','fr'] : [data?.language_mode || 'en'])}
                    onOpen={handleChangeLanguage}
                    variant="dark"
                />
            )}
            {/* Save Indicator */}
            {/* Draft autosave pill ("Saving…/Saved") belongs to the private
                feedback form. On the Auto Flow gift screen there is no draft
                and no save action, so it is stale UI — suppressed. */}
            <SaveIndicator status={lrAutoStage ? null : saveStatus} />

            <div className={`w-full max-w-md text-center space-y-3 mx-auto${isV2 ? ' guest-v2' : ''}`} data-guest-version={isV2 ? 'v2' : 'v1'}>
                {/* Draft Recovery Banner */}
                {pendingDraft && (
                    <RecoveryBanner onResume={handleResumeDraft} onDiscard={handleDiscardDraft} t={t} />
                )}

                {/* v3.5.21b — Compact header: logo + restaurant name only.
                    Per user feedback: dropped "WELCOME TO" eyebrow and the
                    location-name subtitle so the action card (and the
                    circular CTA inside it) sits higher on small viewports
                    and never gets clipped by the iOS browser chrome. */}
                {screen !== 'language' && logoUrl && (
                    <div className="flex flex-col items-center gap-2">
                        <img src={logoUrl} alt="Logo" className={`${logoClass} object-contain rounded-lg`} data-testid="review-shield-logo" />
                        {data.organization_name && (
                            <h2 className="text-lg font-bold text-white" data-testid="review-shield-org-name">
                                {data.organization_name}
                            </h2>
                        )}
                    </div>
                )}

                {/* No-logo fallback: just the org name so we don't end up
                    with an empty space above the action card. */}
                {screen !== 'language' && !logoUrl && data.organization_name && (
                    <h2 className="text-lg font-bold text-white" data-testid="review-shield-org-name">
                        {data.organization_name}
                    </h2>
                )}

                {/* Language Selection Screen — shared gate (handles 1, 2, or 3 languages) */}
                {screen === 'language' && (() => {
                    const enabled = Array.isArray(data?.enabled_languages) && data.enabled_languages.length
                        ? data.enabled_languages
                        : (data?.language_mode === 'bilingual' ? ['en','fr'] : [data?.language_mode || 'en']);
                    return (
                        <LanguageGate
                            enabled={enabled}
                            onSelect={handleLanguageSelect}
                            variant="dark"
                            orgName={data?.organization_name}
                            locationName={data?.location_name}
                            logoUrl={logoUrl}
                            logoSize={data?.logo_size || 'medium'}
                            embedded
                            cardStyle={cardStyle}
                        />
                    );
                })()}


                {/* Rating Screen */}
                {screen === 'rating' && (
                    <div className="space-y-6 animate-in fade-in duration-300" data-testid="review-shield-rating-screen">
                        <div className={`${cardSurface} rounded-2xl p-6`}>
                            <h1 className="text-xl font-semibold text-white mb-6">{vt('guestRatingQuestion')}</h1>
                            <div className="flex justify-center gap-2">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <button key={star} onClick={() => handleRatingSelect(star)} onMouseEnter={() => setHoveredStar(star)} onMouseLeave={() => setHoveredStar(0)} className="transition-all duration-200 hover:scale-125 focus:outline-none" data-testid={`review-shield-star-${star}`}>
                                        <Star className="w-11 h-11" fill={(hoveredStar || rating) >= star ? '#FBBF24' : 'transparent'} stroke={(hoveredStar || rating) >= star ? '#FBBF24' : '#64748b'} strokeWidth={1.5} />
                                    </button>
                                ))}
                            </div>
                            <p className="text-slate-500 text-xs mt-4">{t('reviewShield.guestTapStar')}</p>
                        </div>
                        {showConsultCard && <MedicalConsultCard cardId={cardId} />}
                    </div>
                )}

                {/* Positive Experience — v4.34.7 calm hospitality refinement.
                    Brief (production direction):
                      • REMOVED: progress bar / "Final Step" label / circular
                        glowing-orb CTA / halo / radial-glow pulse / oversized
                        attention-grabbing treatment.
                      • Thank-you confirmation is now the PRIMARY message —
                        the Review Shield interaction is acknowledged BEFORE
                        any public-review option is surfaced.
                      • A single calm rectangular button — "Continue to Public
                        Reviews" — is the optional, visually-secondary CTA.
                      • The "Send private feedback instead" link remains as a
                        neutral peer to the public-review choice.
                    Multi-destination orgs still get the neutral picker
                    rendered below; single-destination orgs continue via the
                    backend-provided redirect_url. */}
                {/* ─────────────────────────────────────────────────
                    Loyal Review — customer-facing sub-screens.
                    Only rendered when the card enabled Loyal Review and
                    has a source voucher configured. The existing
                    'positive' screen below remains untouched. */}
                {screen === 'lrChoice' && (
                    <div className="space-y-4 animate-in fade-in duration-300" data-testid="lr-choice-screen">
                        <div className={`${cardSurface} rounded-2xl p-6 sm:p-7`}>
                            <h2 className="text-2xl font-bold text-white text-center mb-2">{t('loyalReview.choice.heading')}</h2>
                            <p className="text-slate-300 text-center mb-6">{t('loyalReview.choice.subheading')}</p>
                            <div className="space-y-3">
                                <button
                                    type="button"
                                    onClick={() => setScreen('lrEmail')}
                                    className="w-full py-4 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-lg transition-colors shadow-lg shadow-emerald-500/20"
                                    data-testid="lr-choice-gift-btn"
                                >
                                    {t('loyalReview.choice.getGift')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        // v4.44 — The guest was OFFERED a voucher
                                        // (LR enabled + positive rating) but is
                                        // choosing to skip the gift and go
                                        // straight to leaving a public review.
                                        // Record this decision on the feedback
                                        // row so the Operational Base can
                                        // differentiate "declined" from "off"
                                        // (LR feature disabled) and "issued".
                                        // sendBeacon = fire-and-forget so the
                                        // continue flow is never delayed.
                                        try {
                                            if (feedbackId && typeof navigator !== 'undefined' && navigator.sendBeacon) {
                                                const payload = new Blob(
                                                    [JSON.stringify({ feedback_id: feedbackId })],
                                                    { type: 'application/json' },
                                                );
                                                navigator.sendBeacon(
                                                    `${API_BASE}/api/review-shield/${cardId}/voucher-decline`,
                                                    payload,
                                                );
                                            }
                                        } catch (_) { /* never block on beacon */ }
                                        handleContinueTap();
                                    }}
                                    disabled={submitting}
                                    className="w-full py-4 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold text-lg transition-colors shadow-lg shadow-blue-600/20 disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="lr-choice-review-btn"
                                >
                                    {t('loyalReview.choice.leaveReview')}
                                </button>
                            </div>
                            {/* Clinics only: the referral card — the same voucher,
                                for a friend. Every other vertical's thank-you
                                screen is unchanged. */}
                            {showReferralCard && (
                                <MedicalReferralCard cardId={cardId} feedbackId={feedbackId} />
                            )}
                        </div>
                    </div>
                )}
                {screen === 'lrEmail' && (
                    <div className="space-y-4 animate-in fade-in duration-300" data-testid="lr-email-screen">
                        <div className={`${cardSurface} rounded-2xl p-6 sm:p-7`}>
                            <h2 className="text-2xl font-bold text-white text-center mb-2">{t('loyalReview.email.heading')}</h2>
                            <p className="text-slate-300 text-center mb-6">{t('loyalReview.email.body')}</p>
                            <input
                                type="email"
                                value={lrEmail}
                                onChange={(e) => setLrEmail(e.target.value)}
                                placeholder={t('loyalReview.email.placeholder')}
                                className="w-full px-4 py-3 rounded-lg bg-slate-800 text-white placeholder-slate-500 border border-slate-700 focus:border-emerald-500 focus:outline-none mb-3"
                                data-testid="lr-email-input"
                            />
                            {/* Voucher T&C — mandatory. Without acceptance the
                                submit button is disabled AND the backend
                                refuses the request even if the client is
                                tampered with. */}
                            <div className="flex items-start gap-3 mb-3 text-left">
                                <input
                                    type="checkbox"
                                    id="lr-tc-accept-checkbox"
                                    checked={lrTcAccepted}
                                    onChange={(e) => setLrTcAccepted(e.target.checked)}
                                    className="mt-1 h-4 w-4 accent-emerald-500 cursor-pointer flex-shrink-0"
                                    data-testid="lr-tc-accept-checkbox"
                                />
                                <label
                                    htmlFor="lr-tc-accept-checkbox"
                                    className="text-xs text-slate-300 leading-snug cursor-pointer"
                                >
                                    {t('loyalReview.terms.accept')}
                                    {' '}
                                    <button
                                        type="button"
                                        onClick={() => setLrShowTerms(true)}
                                        className="underline text-emerald-400 hover:text-emerald-300"
                                        data-testid="lr-tc-view-link"
                                    >
                                        {t('loyalReview.terms.viewLink', 'View Terms & Conditions')}
                                    </button>
                                </label>
                            </div>
                            {lrError && (
                                <p className="text-rose-400 text-sm text-center mb-2" data-testid="lr-email-error">{lrError}</p>
                            )}
                            <button
                                type="button"
                                onClick={handleLrIssueVoucher}
                                disabled={lrSubmitting || !lrTcAccepted}
                                className="w-full py-4 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid="lr-email-continue-btn"
                            >
                                {t('loyalReview.email.continue')}
                            </button>
                        </div>
                        {lrShowTerms && (
                            <div
                                className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
                                data-testid="lr-tc-dialog"
                                role="dialog"
                                aria-modal="true"
                                onClick={() => setLrShowTerms(false)}
                            >
                                <div
                                    className="bg-slate-900 rounded-2xl p-6 sm:p-7 max-w-lg w-full max-h-[85vh] overflow-y-auto"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <h3
                                        className="text-xl font-bold text-white mb-4"
                                        data-testid="lr-tc-dialog-title"
                                    >
                                        {t('loyalReview.terms.title', 'Voucher Terms & Conditions')}
                                    </h3>
                                    <div
                                        className="text-sm text-slate-200 whitespace-pre-line leading-relaxed mb-6"
                                        data-testid="lr-tc-dialog-body"
                                    >
                                        {t('loyalReview.terms.body', {
                                            merchant: data.organization_name
                                                || t('loyalReview.terms.merchantFallback', 'the merchant'),
                                        })}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setLrShowTerms(false)}
                                        className="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold transition-colors"
                                        data-testid="lr-tc-dialog-close-btn"
                                    >
                                        {t('loyalReview.terms.close', 'Close')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {screen === 'lrVoucher' && lrVoucher && (
                    <div className="space-y-4 animate-in fade-in duration-300" data-testid="lr-voucher-screen">
                        <div className={`${cardSurface} rounded-2xl p-6 sm:p-7 text-center`}>
                            <div className="mb-4">
                                <div className="text-slate-400 text-sm uppercase tracking-wide mb-1">
                                    {t('loyalReview.voucher.redeemableFromLabel')}
                                </div>
                                <div className="text-white text-lg font-medium" data-testid="lr-voucher-redeemable-from">
                                    {lrVoucher.redeemable_from
                                        ? new Date(lrVoucher.redeemable_from).toLocaleDateString()
                                        : '—'}
                                </div>
                            </div>
                            {/* Voucher card — client-side rendered so it can also
                                be saved to the device photo library via canvas. */}
                            <div
                                ref={lrVoucherCardRef}
                                className={`my-6 py-6 px-4 rounded-xl border-y border-slate-700${lrDesign?.bg ? '' : ' bg-gradient-to-br from-emerald-900/60 to-slate-900/80'}`}
                                style={lrDesign?.bg ? { background: lrDesign.bg } : undefined}
                            >
                                {lrVoucher.logo_url && (
                                    <img src={lrVoucher.logo_url} alt="" className="h-14 mx-auto object-contain mb-3" data-testid="lr-voucher-logo" />
                                )}
                                {lrVoucher.organization_name && (
                                    <div className="text-slate-300 text-xs uppercase tracking-[0.25em] mb-3" style={lrTextStyle}>
                                        {lrVoucher.organization_name}
                                    </div>
                                )}
                                {lrVoucher.value != null && (
                                    <div className="mb-2">
                                        <span className="text-emerald-300 text-6xl font-bold leading-none" style={lrAccentStyle}>
                                            {`$${Number(lrVoucher.value).toFixed(0)}`}
                                        </span>
                                        <div className="flex items-center justify-center gap-3 mt-2">
                                            <span className="h-px w-8 bg-slate-600" />
                                            <span className="text-sm text-slate-300" style={lrTextStyle}>{t('loyalReview.voucher.gift', 'Gift')}</span>
                                            <span className="h-px w-8 bg-slate-600" />
                                        </div>
                                    </div>
                                )}
                                <div className="text-xs text-slate-400 uppercase tracking-wide mb-2 mt-3" style={lrMutedStyle}>Code</div>
                                <div className="text-3xl font-mono font-bold text-emerald-400" style={lrAccentStyle} data-testid="lr-voucher-code">
                                    {lrVoucher.code}
                                </div>
                                {/* Expiry — AUTO FLOW ONLY: dropped. Frees the
                                    vertical space the status block needs on a
                                    short phone. Manual path keeps it. */}
                                {lrVoucher.expires_at && !lrAutoStage && (
                                    <div className="text-xs text-slate-400 mt-3">
                                        {t('loyalReview.voucher.expiresOn', 'Expires')}
                                        {' '}
                                        {new Date(lrVoucher.expires_at).toLocaleDateString()}
                                    </div>
                                )}
                                {/* Redemption QR — compact strip; lookup only, staff PIN still required.
                                    AUTO FLOW ONLY: dropped. The guest never
                                    saves or shows this screen (no Save button,
                                    it self-advances), and the QR's height
                                    pushed the status message below the fold on
                                    a real phone. The status takes its place.
                                    The manual path keeps the QR untouched. */}
                                {lrAutoStage ? (
                                    <>
                                        {/* Headline ALONE in the pulsing box; the ring
                                            glows, the text itself never dims. */}
                                        {/* `key` per stage REMOUNTS this box instead of
                                            mutating its text in place. Safari leaves a
                                            ghost of the old text when the content of an
                                            element that (a) sits over a backdrop-filter
                                            surface and (b) is losing a box-shadow
                                            animation changes — which is exactly what the
                                            Stage 1 -> Stage 2 swap did. translateZ(0)
                                            keeps it on its own layer. */}
                                        <div key={`lr-stage-${lrAutoStage}`}
                                            className={`mt-4 rounded-xl px-4 py-4 bg-slate-900/50 border border-white/10 [transform:translateZ(0)] flex flex-col items-center justify-center text-center ${lrAutoStage === 'registering' ? 'animate-status-pulse' : ''}`}
                                            role="status" aria-live="polite" data-testid="lr-auto-flow-status">
                                            {lrAutoStage === 'registering' ? (
                                                <p className="text-white font-bold text-base uppercase tracking-wide leading-tight max-w-full [text-indent:0.025em]" data-testid="lr-auto-flow-stage1">
                                                    {t('loyalReview.autoFlow.registeringGift', 'Registering your gift')}
                                                </p>
                                            ) : (
                                                /* Both stage headlines share one size (16px) — Stage 2
                                                   replaces Stage 1 in place, so any size difference
                                                   reads as a jump. 16px also keeps the unbreakable
                                                   "CONGRATULATIONS!" off the box edges at 390px. */
                                                <p className="text-white font-bold text-base uppercase tracking-wide leading-tight max-w-full [text-indent:0.025em]" data-testid="lr-auto-flow-stage2">
                                                    {t('loyalReview.autoFlow.congrats', 'Congratulations!')}
                                                </p>
                                            )}
                                        </div>
                                        {/* Sub-line stands on its own — no container. */}
                                        {/* Own slow fade (3.1s) — deliberately out of phase with
                                            the box's 1.9s ring so the two never beat together. */}
                                        <p key={`lr-sub-${lrAutoStage}`}
                                            className="text-white text-sm mt-3 leading-snug animate-sub-fade [transform:translateZ(0)]"
                                            data-testid="lr-auto-flow-subline">
                                            {lrAutoStage === 'registering'
                                                ? t('loyalReview.autoFlow.continuingShortly', 'Continuing to public review shortly.')
                                                : t('loyalReview.autoFlow.giftOnItsWay', 'Your gift card is on its way!')}
                                        </p>
                                    </>
                                ) : lrVoucher.qr_token && (
                                    <div className="mt-4 rounded-xl bg-white flex items-center gap-3 p-2.5 text-left" data-testid="lr-voucher-qr-panel">
                                        <QRCodeSVG
                                            value={`${API_BASE}/voucher/scan/${lrVoucher.qr_token}`}
                                            size={92}
                                            level="M"
                                            marginSize={2}
                                            className="shrink-0"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-slate-900 font-bold text-xs uppercase tracking-wide">
                                                {t('vouchers.scanToRedeem', 'Scan to Redeem')}
                                            </p>
                                            <p className="text-slate-500 text-[11px] mt-1 leading-snug">
                                                {t('vouchers.scanStripNote', 'Staff scan · one-time use')}
                                            </p>
                                            <div className="mt-2 h-1 w-10 rounded-full bg-emerald-400" style={lrDesign?.accent ? { background: lrDesign.accent } : undefined} />
                                        </div>
                                    </div>
                                )}
                            </div>
                            <p className="text-slate-300 mb-4" data-testid="lr-voucher-next-visit-note">
                                {t('loyalReview.voucher.redeemableTomorrow', { value: Number(lrVoucher.value || 0).toFixed(0) })}
                            </p>

                            {lrAutoStage === 'registered' && (
                                <button type="button" onClick={handleContinueTap}
                                    className="mb-3 text-xs font-semibold text-emerald-200/80 hover:text-white underline underline-offset-2"
                                    data-testid="lr-auto-flow-continue-now">
                                    {t('loyalReview.autoFlow.continueNow', 'Continue now')}
                                </button>
                            )}

                            {/* Save action — hidden entirely in Auto Flow: the
                                voucher is emailed by the issuance pipeline, so
                                there is nothing to rescue from this screen and a
                                save button would only delay the review.
                                Manual mode is unchanged: before save, primary
                                amber CTA; after save, a static confirmation
                                panel (no shadow/hover/cursor) so it reads as
                                "done" instead of "another action to take".
                                Both variants share box dimensions (py-4,
                                min-h-[60px], text-lg, mb-3) — no layout jump. */}
                            {lrAutoStage ? null : lrSaved ? (
                                <div
                                    className="w-full py-4 min-h-[60px] rounded-xl bg-emerald-900/40 border border-emerald-700/50 text-emerald-200 font-semibold text-lg mb-3 flex items-center justify-center gap-2"
                                    role="status"
                                    aria-live="polite"
                                    data-testid="lr-voucher-saved-confirmation"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 0 1 0 1.414l-8 8a1 1 0 0 1-1.414 0l-4-4a1 1 0 1 1 1.414-1.414L8 12.586l7.293-7.293a1 1 0 0 1 1.414 0z" clipRule="evenodd" />
                                    </svg>
                                    <span>
                                        {lrShareCapable
                                            ? t('loyalReview.voucher.savedToCameraRoll', 'Gift Saved to Camera Roll')
                                            : t('loyalReview.voucher.giftSavedDownloaded', 'Gift Saved')}
                                    </span>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={handleSaveVoucherToPhotos}
                                    disabled={lrSaving}
                                    className="w-full py-4 min-h-[60px] rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-900 shadow-lg shadow-amber-500/20 font-semibold text-lg transition-colors mb-3 disabled:opacity-60 border border-transparent"
                                    data-testid="lr-voucher-save-btn"
                                >
                                    {lrSaving
                                        ? t('loyalReview.voucher.saving', 'Saving…')
                                        : lrShareCapable
                                            ? t('loyalReview.voucher.saveToPhotos', 'Save Voucher to Photos')
                                            : t('loyalReview.voucher.downloadVoucher', 'Download Voucher')}
                                </button>
                            )}

                            {/* Continue CTA — direct redirect, no
                                intermediate "thank you" screen. Once
                                the voucher has been saved, this button
                                is promoted to the primary emerald CTA
                                and its label becomes destination-
                                aware ("Continue to Google Review", etc).
                                Compliance: the label is a neutral
                                navigation cue only. It never implies
                                the reward is contingent on the review. */}
                            {/* Buttons stay out during Stage 1 — there is nothing
                                to skip ahead to while the gift card is still
                                being registered. */}
                            {lrAutoStage !== 'registering' && (
                                <>
                            <button
                                type="button"
                                onClick={handleContinueTap}
                                disabled={submitting}
                                className={
                                    'w-full py-4 rounded-xl font-semibold text-lg transition-colors mb-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-white ' +
                                    (lrSaved
                                        ? 'bg-emerald-500 hover:bg-emerald-600 shadow-emerald-500/20'
                                        : 'bg-blue-600 hover:bg-blue-500 shadow-blue-600/20')
                                }
                                data-testid="lr-voucher-leave-review-btn"
                            >
                                {lrSaved
                                    ? getContinueCtaLabel(t, data?.platform)
                                    : t('loyalReview.voucher.leavePublicReview', 'Leave a Public Review')}
                            </button>
                            <button
                                type="button"
                                onClick={() => window.location.reload()}
                                className="w-full py-3 rounded-xl text-slate-400 hover:text-slate-200 font-medium transition-colors"
                                data-testid="lr-voucher-done-btn"
                            >
                                {t('loyalReview.voucher.done', 'Done')}
                            </button>
                                </>
                            )}
                        </div>
                    </div>
                )}
                {screen === 'positive' && (
                    <div className="space-y-4 animate-in fade-in duration-300" data-testid="review-shield-positive-screen">
                        <div className={`${cardSurface} rounded-2xl p-6 sm:p-7 relative`}>
                            {!destinationsExpanded && (
                                <>
                                    {/* Thank-you confirmation — calm, hospitality-grade primary message */}
                                    <div className="flex flex-col items-center text-center mb-5">
                                        <div className="w-14 h-14 bg-emerald-500/15 rounded-full flex items-center justify-center mb-4" data-testid="positive-thankyou-check">
                                            <svg className="w-7 h-7 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <h1 className="text-xl sm:text-2xl font-semibold text-white mb-2" data-testid="positive-thankyou-title">
                                            {t('reviewShield.guestThankYou', 'Thank you for your feedback.')}
                                        </h1>
                                        <p className="text-slate-300 text-sm sm:text-base" data-testid="positive-share-prompt">
                                            {experienceMode
                                                ? t('reviewShield.guestFeedbackOptional', 'Would you like to share any details?')
                                                : t('reviewShield.guestPublicSharePrompt', 'Would you like to share your experience publicly?')}
                                        </p>
                                    </div>

                                    {highlightOptions.length > 0 && (
                                        <div className="mb-5" data-testid="review-shield-highlights">
                                            <p className="text-sm font-medium text-white mb-3">{vt('highlightsQuestion')}</p>
                                            <div className="flex flex-wrap gap-2 justify-center">
                                                {highlightOptions.map((h) => {
                                                    const on = highlightsArr.includes(h);
                                                    return (
                                                        <button
                                                            key={h}
                                                            type="button"
                                                            aria-pressed={on}
                                                            data-testid={`review-shield-highlight-${h}`}
                                                            onClick={() => setHighlightsArr((prev) => (
                                                                on ? prev.filter((x) => x !== h) : [...prev, h]
                                                            ))}
                                                            className={`rounded-full border px-3.5 py-2 text-sm transition-colors ${on ? 'bg-emerald-500/15 border-emerald-500/60 text-white' : 'bg-white/5 border-white/10 text-slate-200 hover:bg-white/10'}`}
                                                        >
                                                            {t(`reviewShield.highlightLabels.${h}`, h)}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Stars beneath the thank-you — read-only echo of the rating they just gave */}
                                    <div className="flex justify-center gap-1.5 mb-5" data-testid="positive-stars-readout">
                                        {[1, 2, 3, 4, 5].map((s) => (
                                            <Star
                                                key={s}
                                                className="w-5 h-5"
                                                fill={s <= rating ? '#FBBF24' : 'transparent'}
                                                stroke={s <= rating ? '#FBBF24' : 'rgba(255,255,255,0.4)'}
                                                strokeWidth={1.5}
                                            />
                                        ))}
                                    </div>

                                    {/* Optional standard rectangular CTA — visually secondary to the
                                        thank-you message. No glow, no pulse, no oversized treatment.
                                        Experience mode has no public-review platform, so it offers a
                                        neutral "share feedback" action instead of "Continue to Public
                                        Reviews". */}
                                    {experienceMode ? (
                                        <Button
                                            onClick={() => { setCategory(''); setScreen('feedback'); }}
                                            size="lg"
                                            className="w-full py-5 text-base font-semibold rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white transition-colors duration-200"
                                            data-testid="review-shield-experience-feedback-btn"
                                        >
                                            {t('reviewShield.guestPrivateFeedback', 'Share private feedback')}
                                        </Button>
                                    ) : (
                                    <Button
                                        onClick={handleContinueTap}
                                        disabled={submitting}
                                        size="lg"
                                        className="w-full py-5 text-base font-semibold rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white transition-colors duration-200 disabled:opacity-40"
                                        data-testid="review-shield-continue-btn"
                                    >
                                        {submitting ? (
                                            <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                        ) : null}
                                        {t('reviewShield.guestContinueToPublicReviews', 'Continue to Public Reviews')}
                                    </Button>
                                    )}

                                    {/* Private feedback fallback — neutral underline, dim color.
                                        Sits as a peer to the public-review CTA so the guest never
                                        feels automatically funneled. Hidden in Experience mode where
                                        the primary CTA already leads to feedback. */}
                                    {!experienceMode && (
                                    <div className="flex justify-center mt-4">
                                        <button
                                            onClick={() => { setCategory(''); setScreen('feedback'); }}
                                            className="text-slate-300 hover:text-white text-sm underline underline-offset-4 decoration-slate-500 hover:decoration-white transition-colors"
                                            data-testid="review-shield-private-feedback-link"
                                        >
                                            {t('reviewShield.guestPrivateFeedback')}
                                        </button>
                                    </div>
                                    )}

                                    {showReferralCard && (
                                        <MedicalReferralCard cardId={cardId} feedbackId={feedbackId} />
                                    )}
                                </>
                            )}

                            {/* Destination picker (expanded only when org has multi-destinations
                                AND user already tapped the calm rectangular CTA). Existing neutral
                                picker — no platform dominance, equal visual hierarchy. */}
                            {destinationsExpanded && (
                                <div className="my-3">
                                    <GlobalReviewDestinations cardId={cardId} onSelect={handlePositiveSubmit} submitting={submitting} t={t} feedbackId={feedbackId} setFeedbackId={setFeedbackId} />
                                    <button
                                        onClick={() => setDestinationsExpanded(false)}
                                        className="mt-4 text-slate-400 hover:text-slate-200 text-xs underline underline-offset-2 mx-auto block"
                                        data-testid="review-shield-destinations-back"
                                    >
                                        {t('common.back', 'Back')}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Category / Custom Questions Screen */}
                {screen === 'category' && (
                    <div className="space-y-5 animate-in fade-in duration-300" data-testid="review-shield-category-screen">
                        <div className={`${cardSurface} rounded-2xl p-6`}>
                            <div className="flex justify-center mb-3">
                                {[1,2,3,4,5].map((s) => <Star key={s} className="w-5 h-5" fill={s <= rating ? '#FBBF24' : 'transparent'} stroke={s <= rating ? '#FBBF24' : '#64748b'} strokeWidth={1.5} />)}
                            </div>
                            <h1 className="text-xl font-semibold text-white mb-5">{t('reviewShield.guestImproveQuestion')}</h1>
                            <div className="space-y-3">
                                {hasOrgCategories ? (
                                    orgCategories.map((q) => {
                                        const qText = localizedQuestion(q);
                                        return (
                                        <button key={q.id} onClick={() => handleCategorySelect(q.category_id || 'other', qText)} className="w-full flex items-center justify-between px-5 py-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-emerald-500/50 rounded-xl text-white transition-all duration-200 group" data-testid={`review-shield-question-${q.id}`}>
                                            <span className="font-medium text-left">{qText}</span>
                                            <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-emerald-400 transition-colors shrink-0 ml-2" />
                                        </button>
                                        );
                                    })
                                ) : (
                                    defaultCategories.map((cat) => {
                                        const accent = getCategoryAccent(cat.id);
                                        const AccentIcon = accent.Icon;
                                        return (
                                        <button key={cat.id} onClick={() => handleCategorySelect(cat.id)} className="w-full flex items-center justify-between px-5 py-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-emerald-500/50 rounded-xl text-white transition-all duration-200 group" data-testid={`review-shield-category-${cat.id}`}>
                                            <span className="flex items-center gap-3">
                                                {isV2 ? (
                                                    <span className={`flex items-center justify-center w-10 h-10 rounded-xl ${accent.chip}`}>
                                                        <AccentIcon className={`w-5 h-5 ${accent.text}`} strokeWidth={1.75} />
                                                    </span>
                                                ) : (
                                                    <span className="text-lg">{cat.icon}</span>
                                                )}
                                                <span className="font-medium">{t(`reviewShield.guestCategory${cat.id.charAt(0).toUpperCase() + cat.id.slice(1)}`, cat.id)}</span>
                                            </span>
                                            <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-emerald-400 transition-colors" />
                                        </button>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                        <button onClick={() => { setRating(0); setScreen('rating'); }} className="text-slate-500 hover:text-slate-300 text-sm flex items-center gap-1 mx-auto transition-colors">
                            <ArrowLeft className="w-3 h-3" /> {t('reviewShield.guestChangeRating')}
                        </button>
                    </div>
                )}

                {/* Feedback Screen */}
                {screen === 'feedback' && (
                    <div className="space-y-5 animate-in fade-in duration-300" data-testid="review-shield-feedback-screen">
                        <div className={`${cardSurface} rounded-2xl p-6`}>
                            <MessageSquare className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
                            <h1 className="text-xl font-semibold text-white mb-1">{t('reviewShield.guestFeedbackTitle')}</h1>
                            <p className="text-slate-400 text-sm mb-5">{t('reviewShield.guestFeedbackOptional')}</p>
                            {error === 'submit' && <p className="text-red-400 text-sm mb-3">{t('reviewShield.guestSubmitError')}</p>}
                            <div className="space-y-4 text-left">
                                <Textarea placeholder={vt('guestMessagePlaceholder')} value={message} onChange={(e) => setMessage(e.target.value)} className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 min-h-[100px] rounded-xl resize-none focus:border-emerald-500/50" data-testid="review-shield-message-input" />
                                {/* v1.2 — Photo attachment slot. Sits between the comment box and
                                    the contact field per spec. Transient: photos travel with the
                                    submission and are discarded by the backend after email send. */}
                                {photosEnabled && <PhotoAttach photos={photos} onChange={setPhotos} helperText={photoHelper || undefined} noticeText={photoNotice || undefined} />}
                                <div>
                                    <Input type="email" placeholder={t('reviewShield.guestEmailPlaceholder')} value={email} onChange={(e) => setEmail(e.target.value)} className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 rounded-xl focus:border-emerald-500/50" data-testid="review-shield-email-input" />
                                    <p className="text-slate-500 text-xs mt-1.5 pl-1">{t('reviewShield.guestEmailHint')}</p>
                                </div>
                                <Button onClick={handleSubmitFeedback} disabled={!message.trim() || submitting} size="lg" className="w-full py-5 text-base font-semibold rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg transition-all duration-300 disabled:opacity-40" data-testid="review-shield-submit-btn">
                                    {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Send className="w-5 h-5 mr-2" />}
                                    {t('reviewShield.guestSubmitFeedback')}
                                </Button>
                                <Button onClick={handleSubmitRatingOnly} disabled={submitting} size="lg" className="w-full py-5 text-base font-semibold rounded-xl bg-blue-600 hover:bg-blue-700 text-white shadow-lg transition-all duration-300 disabled:opacity-40" data-testid="review-shield-submit-rating-only-btn">
                                    {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Star className="w-5 h-5 mr-2" />}
                                    {t('reviewShield.guestSubmitRatingOnly')}
                                </Button>
                            </div>
                        </div>
                        {rating <= 3 && (
                            <button onClick={() => { setCategory(''); setScreen('category'); }} className="text-slate-500 hover:text-slate-300 text-sm flex items-center gap-1 mx-auto transition-colors">
                                <ArrowLeft className="w-3 h-3" /> {t('reviewShield.guestBack')}
                            </button>
                        )}
                    </div>
                )}

                {/* v4.38.0 — Pickup / Delivery contextual screens.
                  * These screens are reachable ONLY when card.experience_type
                  * is 'pickup' or 'delivery'. Dine-in flow never touches them. */}

                {/* Screen 2 — When did you receive your meal? */}
                {screen === 'mealTime' && (
                    <div className="space-y-5 animate-in fade-in duration-300" data-testid="review-shield-meal-time-screen" data-insight-id="review-shield-takeout-mealtime">
                        <div className={`${cardSurface} rounded-2xl p-6`}>
                            <h1 className="text-xl font-semibold text-white mb-5">{t('reviewShield.mealTime.question')}</h1>
                            <div className="space-y-3">
                                {['<15', '15_30', '30_60', '>60'].map((opt) => (
                                    <button
                                        key={opt}
                                        onClick={() => { setMealTime(opt); setScreen('categoriesMulti'); }}
                                        className={`w-full flex items-center justify-between px-5 py-4 border rounded-xl text-white transition-all duration-200 ${mealTime === opt ? 'bg-emerald-500/15 border-emerald-500/50' : 'bg-white/5 hover:bg-white/10 border-white/10 hover:border-emerald-500/50'}`}
                                        data-testid={`review-shield-meal-time-${opt.replace(/[<>]/g,'')}`}
                                    >
                                        <span className="font-medium text-left">{t(`reviewShield.mealTime.${opt}`)}</span>
                                        <ChevronRight className="w-4 h-4 text-slate-500" />
                                    </button>
                                ))}
                            </div>
                        </div>
                        <button onClick={() => { setRating(0); setScreen('rating'); }} className="text-slate-500 hover:text-slate-300 text-sm flex items-center gap-1 mx-auto transition-colors">
                            <ArrowLeft className="w-3 h-3" /> {t('reviewShield.guestChangeRating')}
                        </button>
                    </div>
                )}

                {/* Screen 3 — Multi-select categories per experience type */}
                {screen === 'categoriesMulti' && (
                    <div className="space-y-5 animate-in fade-in duration-300" data-testid="review-shield-categories-multi-screen" data-insight-id="review-shield-takeout-categories">
                        <div className={`${cardSurface} rounded-2xl p-6`}>
                            <div className="flex justify-center mb-3">
                                {[1,2,3,4,5].map((s) => <Star key={s} className="w-5 h-5" fill={s <= rating ? '#FBBF24' : 'transparent'} stroke={s <= rating ? '#FBBF24' : '#64748b'} strokeWidth={1.5} />)}
                            </div>
                            <h1 className="text-xl font-semibold text-white mb-5">
                                {multiLowPath
                                    ? vt('guestImproveQuestion')
                                    : t(`reviewShield.categoriesQuestion.${data?.experience_type || 'pickup'}`)}
                            </h1>
                            <div className="space-y-2">
                                {(multiLowPath
                                    ? (data?.category_options || [])
                                    : data?.experience_type === 'delivery'
                                        ? ['food_temperature','delivery_speed','packaging','order_accuracy','driver_professionalism','food_quality','other']
                                        : ['food_quality','order_accuracy','packaging','pickup_experience','staff_friendliness','value','other']
                                ).map((cat) => {
                                    const checked = categoriesArr.includes(cat);
                                    return (
                                        <button
                                            key={cat}
                                            onClick={() => {
                                                setCategoriesArr((prev) => checked ? prev.filter((c) => c !== cat) : [...prev, cat]);
                                            }}
                                            className={`w-full flex items-center justify-between px-5 py-3.5 border rounded-xl text-white text-left transition-all duration-200 ${checked ? 'bg-emerald-500/15 border-emerald-500/60' : 'bg-white/5 hover:bg-white/10 border-white/10 hover:border-emerald-500/40'}`}
                                            data-testid={`review-shield-category-multi-${cat}`}
                                            aria-pressed={checked}
                                        >
                                            <span className="font-medium">{categoryLabel(cat)}</span>
                                            <span className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${checked ? 'bg-emerald-500 border-emerald-500' : 'border-white/30'}`}>
                                                {checked && (
                                                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                    </svg>
                                                )}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                            {urgentNote && (
                                <p className="mt-4 text-xs text-slate-400 text-center leading-relaxed" data-testid="review-shield-urgent-note-categories">
                                    {urgentNote}
                                </p>
                            )}
                            <Button
                                onClick={() => setScreen('feedbackTakeout')}
                                size="lg"
                                className="w-full mt-5 py-5 text-base font-semibold rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
                                data-testid="review-shield-categories-multi-continue"
                            >
                                {t('reviewShield.continue')}
                            </Button>
                        </div>
                        <button onClick={() => { if (multiLowPath) { setRating(0); setScreen('rating'); } else setScreen('mealTime'); }} className="text-slate-500 hover:text-slate-300 text-sm flex items-center gap-1 mx-auto transition-colors">
                            <ArrowLeft className="w-3 h-3" /> {t('reviewShield.guestBack')}
                        </button>
                    </div>
                )}

                {/* Screen 4 — Optional comment + optional contact */}
                {screen === 'feedbackTakeout' && (
                    <div className="space-y-5 animate-in fade-in duration-300" data-testid="review-shield-feedback-takeout-screen">
                        <div className={`${cardSurface} rounded-2xl p-6 space-y-5`}>
                            <div data-insight-id="review-shield-takeout-comment">
                                <MessageSquare className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
                                <h2 className="text-xl font-semibold text-white text-center mb-3">{vt('comment.heading')}</h2>
                                <Textarea
                                    placeholder={vt('comment.placeholder')}
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 min-h-[100px] rounded-xl resize-none focus:border-emerald-500/50"
                                    data-testid="review-shield-takeout-comment"
                                />
                            </div>
                            {/* v1.2 — Photo attachment slot. Between comment + contact per spec.
                                Hidden when the organization turned photos off. */}
                            {photosEnabled && <PhotoAttach photos={photos} onChange={setPhotos} helperText={photoHelper || undefined} noticeText={photoNotice || undefined} />}
                            <div className="pt-3 border-t border-white/10 space-y-2" data-insight-id="review-shield-takeout-contact">
                                <h3 className="text-sm font-semibold text-white">{t('reviewShield.contact.heading')}</h3>
                                <p className="text-xs text-slate-400 leading-relaxed">{t('reviewShield.contact.explainer')}</p>
                                <Input
                                    type="email"
                                    placeholder={t('reviewShield.contact.email_placeholder')}
                                    value={contactEmail}
                                    onChange={(e) => setContactEmail(e.target.value)}
                                    className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 rounded-xl focus:border-emerald-500/50"
                                    data-testid="review-shield-takeout-contact-email"
                                />
                                <Input
                                    type="tel"
                                    placeholder={t('reviewShield.contact.phone_placeholder')}
                                    value={contactPhone}
                                    onChange={(e) => setContactPhone(e.target.value)}
                                    className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 rounded-xl focus:border-emerald-500/50"
                                    data-testid="review-shield-takeout-contact-phone"
                                />
                            </div>
                            {urgentNote && (
                                <p className="text-xs text-slate-400 text-center leading-relaxed" data-testid="review-shield-urgent-note">
                                    {urgentNote}
                                </p>
                            )}
                            {error === 'submit' && <p className="text-red-400 text-sm text-center">{t('reviewShield.guestSubmitError')}</p>}
                            <Button
                                onClick={handleSubmitTakeoutDelivery}
                                disabled={submitting}
                                size="lg"
                                className="w-full py-5 text-base font-semibold rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40"
                                data-testid="review-shield-takeout-submit"
                            >
                                {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Send className="w-5 h-5 mr-2" />}
                                {t('reviewShield.guestSubmitFeedback')}
                            </Button>
                        </div>
                        <button onClick={() => setScreen('categoriesMulti')} className="text-slate-500 hover:text-slate-300 text-sm flex items-center gap-1 mx-auto transition-colors">
                            <ArrowLeft className="w-3 h-3" /> {t('reviewShield.guestBack')}
                        </button>
                    </div>
                )}

                {/* Private Review — thank you + optional suggestion. No public step. */}
                {screen === 'privateSuggestion' && (
                    <div className="space-y-4 animate-in fade-in duration-300" data-testid="review-shield-private-suggestion-screen">
                        <div className={`${cardSurface} rounded-2xl p-6 sm:p-7`}>
                            <div className="flex flex-col items-center text-center mb-5">
                                <div className="w-14 h-14 bg-emerald-500/15 rounded-full flex items-center justify-center mb-4">
                                    <Check className="w-7 h-7 text-emerald-400" strokeWidth={2.5} />
                                </div>
                                <h1 className="text-xl sm:text-2xl font-semibold text-white mb-2" data-testid="private-review-thankyou-title">
                                    {t('reviewShield.privateThankYou', 'Thank you so much for sharing your thoughts with us.')}
                                </h1>
                                <p className="text-slate-300 text-sm sm:text-base" data-testid="private-review-suggestion-prompt">
                                    {t('reviewShield.privateSuggestionPrompt', 'Do you have any suggestions for us?')}
                                </p>
                            </div>
                            <div className="flex justify-center gap-1.5 mb-5" data-testid="private-review-stars-readout">
                                {[1, 2, 3, 4, 5].map((s) => (
                                    <Star
                                        key={s}
                                        className="w-5 h-5"
                                        fill={s <= rating ? '#FBBF24' : 'transparent'}
                                        stroke={s <= rating ? '#FBBF24' : 'rgba(255,255,255,0.4)'}
                                        strokeWidth={1.5}
                                    />
                                ))}
                            </div>
                            <Textarea
                                placeholder={t('reviewShield.privateSuggestionPlaceholder', 'Your suggestions (optional)')}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 min-h-[100px] rounded-xl resize-none focus:border-emerald-500/50"
                                data-testid="private-review-suggestion-input"
                            />
                            <Button
                                onClick={handleSubmitFeedback}
                                disabled={submitting}
                                size="lg"
                                className="w-full mt-4 py-5 text-base font-semibold rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white transition-colors duration-200"
                                data-testid="private-review-submit-btn"
                            >
                                {submitting
                                    ? <Loader2 className="w-5 h-5 animate-spin" />
                                    : t('reviewShield.privateSuggestionSend', 'Send')}
                            </Button>
                            <button
                                type="button"
                                onClick={handleSubmitRatingOnly}
                                disabled={submitting}
                                className="w-full mt-3 text-slate-400 hover:text-slate-200 text-sm transition-colors"
                                data-testid="private-review-skip-btn"
                            >
                                {t('reviewShield.privateSuggestionSkip', 'Finish without a suggestion')}
                            </button>
                        </div>
                    </div>
                )}

                {/* Confirmation */}
                {screen === 'confirmation' && (
                    <div className="space-y-5 animate-in fade-in duration-300" data-testid="review-shield-confirmation-screen">
                        <div className={`${cardSurface} rounded-2xl p-8`}>
                            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h1 className="text-xl font-semibold text-white mb-2">{t('reviewShield.guestConfirmTitle')}</h1>
                            <p className="text-slate-300 text-sm">{t('reviewShield.guestConfirmMessage')}</p>
                        </div>
                    </div>
                )}

                {/* Promo button — ADDED below the existing buttons of the step
                    the promo names, above the branding footer. Nothing is
                    rendered on any other step, or when no promo is live. */}
                <PromoButtons cardId={cardId} step={screen} lang={i18n.language} />

                {/* Branding */}
                {data.show_yulontap_branding !== false && (
                    <div className="pt-2 flex flex-col items-center gap-2">
                        <CardScanActions cardId={experienceMode ? null : cardId} />
                        {vipSession && (
                            <button
                                onClick={deliverVip}
                                data-testid="vip-customer-btn"
                                className="bg-red-600 hover:bg-red-700 text-white text-sm font-bold px-8 py-2 rounded-full transition-colors shadow-lg shadow-red-600/30"
                            >
                                VIP
                            </button>
                        )}
                    </div>
                )}

                {/* v4.34.17 — Secondary public review link.
                  *
                  * Renders ONLY on the final confirmation screen and ONLY when the
                  * organization/location has a configured public review destination
                  * (data.redirect_url comes from `get_redirect_url(org, platform, loc)`
                  * — the same cascade used everywhere else in the platform).
                  *
                  * Intentionally low-emphasis: muted text + underline, no button
                  * styling, no brand colors. Placed below MY TAP / NEO per spec.
                  *
                  * Analytics: fires `POST /api/global-review/{cardId}/select` which
                  * records a tap with `source='global_review'`, `destination_type=platform`,
                  * organization_id, location_id, timestamp. Fire-and-forget so it
                  * doesn't block the native `target=_blank` navigation. */}
                {screen === 'confirmation' && data?.redirect_url && !data?.private_review_enabled && (
                    <div
                        className="pt-2 mt-2 border-t border-white/10 flex justify-center"
                        data-testid="review-shield-public-review-link-wrap"
                    >
                        <a
                            href={data.redirect_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={t('reviewShield.guestLeavePublicReview', 'Leave a public review')}
                            data-testid="review-shield-public-review-link"
                            onClick={() => {
                                // Fire-and-forget analytics. Native navigation continues
                                // synchronously via the href, avoiding popup blockers.
                                if (data?.platform) {
                                    axios.post(
                                        `${API_BASE}/api/global-review/${cardId}/select?platform=${data.platform}`
                                    ).catch(() => { /* analytics is best-effort */ });
                                }
                            }}
                            className="inline-flex items-center gap-2 text-sm font-normal text-white/75 hover:text-white underline underline-offset-2 transition-colors mt-4"
                        >
                            <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
                            <span>{t('reviewShield.guestLeavePublicReview', 'Leave a public review')}</span>
                        </a>
                    </div>
                )}

            </div>
        </BrandingShell>
    );
};

export default ReviewShield;
